import React, { createContext, useContext, useReducer, useRef, useEffect, useMemo, useCallback } from 'react';

// Action types
const MEDIA_PLAYER_ACTIONS = {
  PLAY_MEDIA: 'PLAY_MEDIA',
  PLAY: 'PLAY',
  PAUSE: 'PAUSE',
  STOP: 'STOP',
  SET_VOLUME: 'SET_VOLUME',
  SET_PROGRESS: 'SET_PROGRESS',
  SET_DURATION: 'SET_DURATION',
  SET_CURRENT_TIME: 'SET_CURRENT_TIME',
  TOGGLE_MINIMIZE: 'TOGGLE_MINIMIZE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_QUEUE: 'SET_QUEUE',
  ADD_TO_QUEUE: 'ADD_TO_QUEUE',
  REMOVE_FROM_QUEUE: 'REMOVE_FROM_QUEUE',
  CLEAR_QUEUE: 'CLEAR_QUEUE',
  NEXT_TRACK: 'NEXT_TRACK',
  PREVIOUS_TRACK: 'PREVIOUS_TRACK',
  SET_REPEAT_MODE: 'SET_REPEAT_MODE',
  SET_SHUFFLE: 'SET_SHUFFLE',
  SET_AUTOPLAY: 'SET_AUTOPLAY'
};

// Repeat modes
export const REPEAT_MODES = {
  OFF: 'off',
  ONE: 'one',
  ALL: 'all'
};

// Initial state
const initialState = {
  // Current media
  currentMedia: null,
  mediaType: null, // 'podcast', 'stream', 'audio'
  
  // Playback state
  isPlaying: false,
  isLoading: false,
  error: null,
  
  // Time and progress
  currentTime: 0,
  duration: 0,
  progress: 0,
  
  // Audio settings
  volume: 1,
  muted: false,
  
  // UI state
  isMinimized: false,
  isVisible: false,
  
  // Queue management
  queue: [],
  currentIndex: 0,
  repeatMode: REPEAT_MODES.OFF,
  shuffle: false,
  shuffleHistory: [],
  autoplay: true,
  
  // History
  playHistory: []
};

// Shuffle array function
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const normalizeMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return '';

  let normalized = url.trim();
  if (!normalized) return '';

  if (normalized.startsWith('//')) {
    normalized = `${window.location.protocol}${normalized}`;
  }

  // Avoid mixed-content playback failures on HTTPS pages.
  if (window.location.protocol === 'https:' && normalized.startsWith('http://')) {
    normalized = normalized.replace(/^http:\/\//i, 'https://');
  }

  return normalized;
};

const normalizeMediaObject = (media = {}) => {
  const candidateUrl =
    media.media_url ||
    media.audio_url ||
    media.url ||
    media.stream_url ||
    media.link ||
    '';

  const normalizedUrl = normalizeMediaUrl(candidateUrl);

  return {
    ...media,
    media_url: normalizedUrl || media.media_url || '',
    url: normalizedUrl || media.url || '',
    stream_url: media.stream_url ? normalizeMediaUrl(media.stream_url) : media.stream_url
  };
};

// Reducer function
const mediaPlayerReducer = (state, action) => {
  switch (action.type) {
    case MEDIA_PLAYER_ACTIONS.PLAY_MEDIA:
      return {
        ...state,
        currentMedia: action.payload.media,
        mediaType: action.payload.type,
        queue: action.payload.queue || [],
        currentIndex: action.payload.index || 0,
        isPlaying: true,
        isVisible: true,
        isLoading: true,
        error: null,
        currentTime: 0,
        progress: 0,
        playHistory: [action.payload.media, ...state.playHistory.slice(0, 9)] // Keep last 10
      };
      
    case MEDIA_PLAYER_ACTIONS.PLAY:
      return {
        ...state,
        isPlaying: true,
        error: null
      };
      
    case MEDIA_PLAYER_ACTIONS.PAUSE:
      return {
        ...state,
        isPlaying: false
      };
      
    case MEDIA_PLAYER_ACTIONS.STOP:
      return {
        ...state,
        isPlaying: false,
        currentMedia: null,
        mediaType: null,
        isVisible: false,
        currentTime: 0,
        progress: 0,
        error: null
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_VOLUME:
      return {
        ...state,
        volume: action.payload,
        muted: action.payload === 0
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_CURRENT_TIME:
      return {
        ...state,
        currentTime: action.payload,
        progress: state.duration > 0 ? (action.payload / state.duration) * 100 : 0
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_DURATION:
      return {
        ...state,
        duration: action.payload,
        isLoading: false
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_PROGRESS:
      return {
        ...state,
        progress: action.payload,
        currentTime: state.duration > 0 ? (action.payload / 100) * state.duration : 0
      };
      
    case MEDIA_PLAYER_ACTIONS.TOGGLE_MINIMIZE:
      return {
        ...state,
        isMinimized: !state.isMinimized
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isPlaying: false
      };

    case MEDIA_PLAYER_ACTIONS.SET_QUEUE:
      return {
        ...state,
        queue: action.payload.queue,
        currentIndex: action.payload.index || 0
      };
      
    case MEDIA_PLAYER_ACTIONS.ADD_TO_QUEUE:
      return {
        ...state,
        queue: [...state.queue, action.payload]
      };
      
    case MEDIA_PLAYER_ACTIONS.REMOVE_FROM_QUEUE:
      return {
        ...state,
        queue: state.queue.filter((_, index) => index !== action.payload)
      };
      
    case MEDIA_PLAYER_ACTIONS.CLEAR_QUEUE:
      return {
        ...state,
        queue: [],
        currentIndex: 0
      };

    case MEDIA_PLAYER_ACTIONS.PLAY_MEDIA:
      return {
        ...state,
        currentMedia: action.payload.media,
        mediaType: action.payload.type,
        queue: action.payload.queue || [],
        currentIndex: action.payload.index || 0,
        isPlaying: true,
        isVisible: true,
        isMinimized: action.payload.type === 'stream' ? false : state.isMinimized, // Start streams expanded
        isLoading: true,
        error: null,
        currentTime: 0,
        progress: 0,
        playHistory: [action.payload.media, ...state.playHistory.slice(0, 9)]
      };
      
    case MEDIA_PLAYER_ACTIONS.NEXT_TRACK:
      if (state.queue.length === 0) return state;
      
      let nextIndex;
      if (state.shuffle) {
        // Handle shuffle mode
        const availableIndices = state.queue
          .map((_, index) => index)
          .filter(index => index !== state.currentIndex && !state.shuffleHistory.includes(index));
        
        if (availableIndices.length === 0) {
          // All tracks played, reset shuffle history
          nextIndex = Math.floor(Math.random() * state.queue.length);
          while (nextIndex === state.currentIndex && state.queue.length > 1) {
            nextIndex = Math.floor(Math.random() * state.queue.length);
          }
        } else {
          nextIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
        }
      } else {
        nextIndex = state.currentIndex + 1;
        if (nextIndex >= state.queue.length) {
          if (state.repeatMode === REPEAT_MODES.ALL) {
            nextIndex = 0;
          } else {
            return { ...state, isPlaying: false };
          }
        }
      }
      
      return {
        ...state,
        currentIndex: nextIndex,
        currentMedia: state.queue[nextIndex],
        shuffleHistory: state.shuffle ? 
          [...state.shuffleHistory, state.currentIndex].slice(-state.queue.length) : 
          state.shuffleHistory,
        currentTime: 0,
        progress: 0,
        isPlaying: state.autoplay
      };
      
    case MEDIA_PLAYER_ACTIONS.PREVIOUS_TRACK:
      if (state.queue.length === 0) return state;
      
      let prevIndex;
      if (state.shuffle && state.shuffleHistory.length > 0) {
        // Get previous from shuffle history
        prevIndex = state.shuffleHistory[state.shuffleHistory.length - 1];
      } else {
        prevIndex = state.currentIndex - 1;
        if (prevIndex < 0) {
          if (state.repeatMode === REPEAT_MODES.ALL) {
            prevIndex = state.queue.length - 1;
          } else {
            return state;
          }
        }
      }
      
      return {
        ...state,
        currentIndex: prevIndex,
        currentMedia: state.queue[prevIndex],
        shuffleHistory: state.shuffle ? 
          state.shuffleHistory.slice(0, -1) : 
          state.shuffleHistory,
        currentTime: 0,
        progress: 0
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_REPEAT_MODE:
      return {
        ...state,
        repeatMode: action.payload
      };
      
    case MEDIA_PLAYER_ACTIONS.SET_SHUFFLE:
      return {
        ...state,
        shuffle: action.payload,
        shuffleHistory: action.payload ? [] : state.shuffleHistory
      };

    case MEDIA_PLAYER_ACTIONS.SET_AUTOPLAY:
      return {
        ...state,
        autoplay: action.payload
      };
      
    default:
      return state;
  }
};

// Create context
const MediaPlayerContext = createContext();

// Provider component
export const MediaPlayerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(mediaPlayerReducer, initialState);
  const audioRef = useRef(null);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      dispatch({
        type: MEDIA_PLAYER_ACTIONS.SET_CURRENT_TIME,
        payload: audio.currentTime
      });
    };

    const handleDurationChange = () => {
      dispatch({
        type: MEDIA_PLAYER_ACTIONS.SET_DURATION,
        payload: audio.duration
      });
    };

    const handleLoadStart = () => {
      dispatch({
        type: MEDIA_PLAYER_ACTIONS.SET_LOADING,
        payload: true
      });
    };

    const handleCanPlay = () => {
      dispatch({
        type: MEDIA_PLAYER_ACTIONS.SET_LOADING,
        payload: false
      });
    };

    const handleError = (e) => {
      dispatch({
        type: MEDIA_PLAYER_ACTIONS.SET_ERROR,
        payload: 'Failed to load media'
      });
    };

    const handleEnded = () => {
      if (state.repeatMode === REPEAT_MODES.ONE) {
        audio.currentTime = 0;
        if (state.autoplay) {
          audio.play();
        }
      } else if (state.queue.length > 0 && state.autoplay) {
        dispatch({ type: MEDIA_PLAYER_ACTIONS.NEXT_TRACK });
      } else {
        dispatch({ type: MEDIA_PLAYER_ACTIONS.PAUSE });
      }
    };

    const handleVolumeChange = () => {
      dispatch({
        type: MEDIA_PLAYER_ACTIONS.SET_VOLUME,
        payload: audio.volume
      });
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('volumechange', handleVolumeChange);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [state.repeatMode, state.queue.length, state.autoplay]);

  // Handle play/pause state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (state.isPlaying && !state.isLoading) {
      audio.play().catch(error => {
        console.error('Playback failed:', error);
        dispatch({
          type: MEDIA_PLAYER_ACTIONS.SET_ERROR,
          payload: 'Failed to play media'
        });
      });
    } else {
      audio.pause();
    }
  }, [state.isPlaying, state.isLoading]);

  // Handle volume changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = state.volume;
    }
  }, [state.volume]);

  // Handle media changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !state.currentMedia) return;

    const mediaUrl = normalizeMediaUrl(
      state.currentMedia.media_url || state.currentMedia.url || state.currentMedia.stream_url || state.currentMedia.link
    );
    if (mediaUrl && audio.src !== mediaUrl) {
      audio.removeAttribute('crossorigin');
      audio.src = mediaUrl;
      audio.volume = state.volume;
      
      // For streams, we might want to handle them differently
      if (state.mediaType === 'stream') {
        // Streams don't use the audio element - they're handled by the StreamPlayer component
        return;
      }
    }
  }, [state.currentMedia, state.volume, state.mediaType]);


  // Stable action creators using useCallback
  const playMedia = useCallback((media, type = 'podcast', queue = [], index = 0) => {
    const normalizedMedia = normalizeMediaObject(media);
    const normalizedQueue = Array.isArray(queue) ? queue.map(normalizeMediaObject) : [];
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.PLAY_MEDIA,
      payload: { media: normalizedMedia, type, queue: normalizedQueue, index }
    });
  }, []);

  const play = useCallback(() => {
    dispatch({ type: MEDIA_PLAYER_ACTIONS.PLAY });
  }, []);

  const pause = useCallback(() => {
    dispatch({ type: MEDIA_PLAYER_ACTIONS.PAUSE });
  }, []);

  const stop = useCallback(() => {
    dispatch({ type: MEDIA_PLAYER_ACTIONS.STOP });
  }, []);

  const setVolume = useCallback((volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.SET_VOLUME,
      payload: clampedVolume
    });
  }, []);

  const seek = useCallback((progress) => {
    const audio = audioRef.current;
    if (audio) {
      const newTime = (progress / 100) * audio.duration;
      if (audio.duration > 0) {
        audio.currentTime = newTime;
      }
      dispatch({
        type: MEDIA_PLAYER_ACTIONS.SET_PROGRESS,
        payload: progress
      });
    }
  }, []);

  const toggleMinimize = useCallback(() => {
    dispatch({ type: MEDIA_PLAYER_ACTIONS.TOGGLE_MINIMIZE });
  }, []);

  const setQueue = useCallback((queue, index = 0) => {
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.SET_QUEUE,
      payload: { queue: Array.isArray(queue) ? queue.map(normalizeMediaObject) : [], index }
    });
  }, []);

  const addToQueue = useCallback((media) => {
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.ADD_TO_QUEUE,
      payload: normalizeMediaObject(media)
    });
  }, []);

  const removeFromQueue = useCallback((index) => {
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.REMOVE_FROM_QUEUE,
      payload: index
    });
  }, []);

  const clearQueue = useCallback(() => {
    dispatch({ type: MEDIA_PLAYER_ACTIONS.CLEAR_QUEUE });
  }, []);

  const nextTrack = useCallback(() => {
    dispatch({ type: MEDIA_PLAYER_ACTIONS.NEXT_TRACK });
  }, []);

  const previousTrack = useCallback(() => {
    dispatch({ type: MEDIA_PLAYER_ACTIONS.PREVIOUS_TRACK });
  }, []);

  const setRepeatMode = useCallback((mode) => {
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.SET_REPEAT_MODE,
      payload: mode
    });
  }, []);

  const toggleShuffle = useCallback(() => {
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.SET_SHUFFLE,
      payload: !state.shuffle
    });
  }, [state.shuffle]);

  const setAutoplay = useCallback((autoplay) => {
    dispatch({
      type: MEDIA_PLAYER_ACTIONS.SET_AUTOPLAY,
      payload: autoplay
    });
  }, []);

  const value = useMemo(() => ({
    ...state,
    playMedia,
    play,
    pause,
    stop,
    togglePlayPause: state.isPlaying ? pause : play,
    setVolume,
    seek,
    toggleMinimize,
    setQueue,
    addToQueue,
    removeFromQueue,
    clearQueue,
    nextTrack,
    previousTrack,
    setRepeatMode,
    toggleShuffle,
    setAutoplay,
    audioRef
  }), [
    state,
    playMedia,
    play,
    pause,
    stop,
    setVolume,
    seek,
    toggleMinimize,
    setQueue,
    addToQueue,
    removeFromQueue,
    clearQueue,
    nextTrack,
    previousTrack,
    setRepeatMode,
    toggleShuffle,
    setAutoplay
  ]);

  return (
    <MediaPlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="metadata" />
    </MediaPlayerContext.Provider>
  );
};

// Custom hook to use the media player context
export const useMediaPlayer = () => {
  const context = useContext(MediaPlayerContext);
  if (!context) {
    throw new Error('useMediaPlayer must be used within a MediaPlayerProvider');
  }
  return context;
};

export default MediaPlayerContext;
